import { Heart, Cake, PartyPopper, Building2, TreePine, Beer, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';

const Services = () => {
  const { t } = useLanguage();
  const services = [
    {
      icon: Heart,
      ...t.services.items.weddings,
      gradient: 'from-primary/30 to-primary/10',
    },
    {
      icon: Cake,
      ...t.services.items.birthdays,
      gradient: 'from-accent/30 to-accent/10',
    },
    {
      icon: PartyPopper,
      ...t.services.items.celebrations,
      gradient: 'from-primary/25 to-accent/25',
    },
    {
      icon: Beer,
      ...t.services.items.oktoberfest,
      gradient: 'from-accent/30 to-primary/30',
    },
    {
      icon: Building2,
      ...t.services.items.indoor,
      gradient: 'from-primary/20 to-primary/10',
    },
    {
      icon: TreePine,
      ...t.services.items.outdoor,
      gradient: 'from-accent/25 to-accent/10',
    },
  ];

  return (
    <section id="services" className="py-24 bg-background relative overflow-hidden">
      <div className="absolute inset-0 festive-gradient opacity-50" />
      <div className="absolute inset-0 bavarian-pattern" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold border border-primary/20">
              <Sparkles className="w-4 h-4" />
              {t.services.badge}
            </div>
            
            <h2 className="text-4xl md:text-5xl font-serif font-bold">
              {t.services.title}
              <span className="block text-primary mt-2">{t.services.titleHighlight}</span>
            </h2>
            
            <p className="text-xl text-foreground/75 max-w-3xl mx-auto leading-relaxed font-medium">
              {t.services.description}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transition-all duration-500 hover:scale-105 border-2 hover:border-primary/50 bg-card overflow-hidden relative"
                >
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <CardContent className="p-8 relative z-10">
                    <div className="space-y-6">
                      {/* Icon */}
                      <div className="inline-flex p-4 rounded-2xl bg-primary/10 group-hover:bg-primary/20 text-primary shadow-lg group-hover:scale-110 transition-all duration-300">
                        <Icon className="w-10 h-10" />
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-2xl font-serif font-bold text-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-foreground/70 leading-relaxed font-medium">
                        {service.description}
                      </p>
                      
                      {/* Features */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        {service.features.map((feature, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="group-hover:bg-primary/20 group-hover:text-primary transition-colors"
                          >
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-card border-2 border-accent/30 rounded-3xl p-8 hover:border-accent/60 transition-all hover:shadow-xl">
              <p className="text-lg text-muted-foreground mb-4">
                {t.services.note}
              </p>
              <p className="text-xl font-semibold">
                {t.services.noteHighlight}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
